/**
 * COL-417: the receipts behind one rent month, with the correction path.
 *
 * Its own component for a specific reason: it owns react-query mutations, and
 * RentPaymentRow renders one strip per month in the schedule. Hooks at row
 * level would mount a query-client subscription for every month whether or not
 * anyone opened it — and would make the whole tab unrenderable in the tests
 * that mount it without a QueryClientProvider. Rendered only while open, the
 * hooks cost nothing until an admin asks for the detail.
 *
 * Correcting a receipt is the ticket's fallback path for a payment entered in
 * error. A receipt is a claim about money that arrived, so a wrong one is
 * corrected or withdrawn at source — never offset by a compensating entry,
 * which is why this ledger has no negative rows. The database re-derives
 * everything downstream: received_cents, the row's status, the tenant's
 * activity log, and any credit that had been carried forward from it.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  useDeleteReceipt,
  useReceiptBankTransactions,
  useUpdateReceipt,
  type PaymentReceipt,
  type ReceiptBankTransaction,
} from "@/hooks/v2/usePaymentReceipts";
import {
  actionBtn,
  chip,
  uppLabel,
  IcEdit,
  IcEye,
  IcTrash,
  IcUpload,
  RowActionsMenu,
  type RowAction,
} from "./tenant-profile-ui";

function centsToEUR(cents?: number | null): string {
  return `\u20ac${((Number(cents) || 0) / 100).toFixed(2)}`;
}

function formatDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
}

export function PaymentReceiptsStrip({
  receipts,
  /** The document attached to the PARENT payment row, when one exists. */
  proof,
  onViewProof,
  onUploadProof,
  busy,
}: {
  receipts: PaymentReceipt[];
  proof?: { file_name?: string | null; created_at?: string | null } | null;
  onViewProof?: (proof: any) => void;
  onUploadProof?: () => void;
  busy?: boolean;
}) {
  const { t } = useLanguage();
  const [editingReceipt, setEditingReceipt] = useState<string | null>(null);
  const [receiptAmount, setReceiptAmount] = useState("");
  const [proofOpen, setProofOpen] = useState<string | null>(null);
  const updateReceipt = useUpdateReceipt();
  const deleteReceipt = useDeleteReceipt();
  // Only fires when a receipt actually came out of the reconciliation queue.
  const { data: bankTxById = {} } = useReceiptBankTransactions(
    receipts.map((r) => r.bank_transaction_id ?? "").filter(Boolean)
  );

  return (
    <div
      style={{
        margin: "0 12px 12px",
        background: "#F8FAFC",
        border: "1px solid #EEF2F6",
        borderRadius: 8,
        padding: "10px 12px",
      }}
    >
      <div style={{ ...uppLabel, marginBottom: 8 }}>
        {t("admin.tenantTabs.receiptsTitle", "Received")} ({receipts.length})
      </div>
      {receipts.map((r) => (
        <div key={r.id} style={{ borderBottom: "1px solid #EEF2F6" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              padding: "6px 0",
              fontSize: 12.5,
            }}
          >
            <div style={{ color: "#0F172A" }}>
              {formatDate(r.received_at) ?? String(r.received_at).slice(0, 10)}
              {r.method ? ` · ${String(r.method).replace(/_/g, " ")}` : ""}
              {r.applies_to === "fee" && (
                <span
                  style={{
                    marginLeft: 6,
                    ...chip("#FEF6E7", "#B45309", "#F7E4BD"),
                  }}
                >
                  {t("admin.tenantTabs.receiptFee", "Mahngebühr")}
                </span>
              )}
              {r.source === "credit_carryover" && (
                <span
                  style={{
                    marginLeft: 6,
                    ...chip("#E6FAF7", "#0F766E", "#B8EBE3"),
                  }}
                >
                  {t("admin.tenantTabs.receiptCredit", "Credit")}
                </span>
              )}
              {r.note && (
                <div style={{ color: "#94A3B8", fontSize: 11.5, marginTop: 2 }}>
                  {r.note}
                </div>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {editingReceipt === r.id ? (
                <>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={receiptAmount}
                    onChange={(e) => setReceiptAmount(e.target.value)}
                    style={{
                      width: 88,
                      padding: "4px 7px",
                      border: "1px solid #CBD5E1",
                      borderRadius: 6,
                      fontSize: 12.5,
                      textAlign: "right",
                    }}
                  />
                  <button
                    style={actionBtn("#0B7268")}
                    disabled={updateReceipt.isPending}
                    onClick={async () => {
                      const cents = Math.round(
                        (parseFloat(receiptAmount.replace(",", ".")) || 0) * 100
                      );
                      if (!(cents > 0)) {
                        toast.error(
                          t(
                            "admin.tenantTabs.receiptAmountInvalid",
                            "Enter an amount greater than €0, or delete the receipt."
                          )
                        );
                        return;
                      }
                      try {
                        await updateReceipt.mutateAsync({
                          id: r.id,
                          amountCents: cents,
                        });
                        setEditingReceipt(null);
                        toast.success(
                          t(
                            "admin.tenantTabs.receiptUpdated",
                            "Receipt corrected."
                          )
                        );
                      } catch (e) {
                        toast.error(e instanceof Error ? e.message : String(e));
                      }
                    }}
                  >
                    {t("admin.tenantTabs.receiptSave", "Save")}
                  </button>
                  <button
                    style={actionBtn("#64748B")}
                    onClick={() => setEditingReceipt(null)}
                  >
                    {t("admin.tenantTabs.receiptCancel", "Cancel")}
                  </button>
                </>
              ) : (
                <>
                  <span
                    style={{
                      fontWeight: 600,
                      fontVariantNumeric: "tabular-nums",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {centsToEUR(r.amount_cents)}
                  </span>
                  {/* Every receipt can show what backs it — that is the whole
                    point of a ledger row. A reconciliation receipt carries the
                    bank's own record, which is stronger evidence than any
                    uploaded document; before this it was the one thing on the
                    screen with no way to see it. */}
                  <button
                    onClick={() =>
                      setProofOpen(proofOpen === r.id ? null : r.id)
                    }
                    aria-expanded={proofOpen === r.id}
                    style={
                      r.bank_transaction_id
                        ? {
                            ...actionBtn("#0B7268"),
                            borderColor: "#C6EEDB",
                            background: "#F4FCF8",
                          }
                        : actionBtn("#64748B")
                    }
                  >
                    <IcEye />
                    {t("admin.tenantTabs.receiptProof", "Proof")}
                  </button>
                  {/* A carried-forward credit is not a claim anyone made — it
                      is derived from the overpaid month and re-derived
                      whenever that month changes. Editing it here would be
                      undone by the next recompute, so it is corrected at its
                      source instead. */}
                  {r.source !== "credit_carryover" && (
                    <RowActionsMenu
                      label={t("admin.tenantTabs.rowActions", "More actions")}
                      actions={[
                        {
                          key: "edit",
                          label: t(
                            "admin.tenantTabs.receiptEditAmount",
                            "Correct amount"
                          ),
                          icon: <IcEdit />,
                          onSelect: () => {
                            setEditingReceipt(r.id);
                            setReceiptAmount((r.amount_cents / 100).toFixed(2));
                          },
                        },
                        {
                          key: "delete",
                          label: t(
                            "admin.tenantTabs.receiptDelete",
                            "Remove receipt…"
                          ),
                          icon: <IcTrash />,
                          disabled: deleteReceipt.isPending,
                          separatorBefore: true,
                          destructive: true,
                          onSelect: async () => {
                            if (
                              !window.confirm(
                                t(
                                  "admin.tenantTabs.receiptDeleteConfirm",
                                  "Remove this receipt? The month reopens for the amount it covered."
                                )
                              )
                            )
                              return;
                            try {
                              await deleteReceipt.mutateAsync(r.id);
                              toast.success(
                                t(
                                  "admin.tenantTabs.receiptDeleted",
                                  "Receipt removed."
                                )
                              );
                            } catch (e) {
                              toast.error(
                                e instanceof Error ? e.message : String(e)
                              );
                            }
                          },
                        } as RowAction,
                      ]}
                    />
                  )}
                </>
              )}
            </div>
          </div>
          {proofOpen === r.id && (
            <ReceiptEvidence
              receipt={r}
              bankTx={
                r.bank_transaction_id
                  ? (bankTxById[r.bank_transaction_id] ?? null)
                  : null
              }
              proof={proof ?? null}
              onViewProof={onViewProof}
              onUploadProof={onUploadProof}
              busy={busy}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function EvidenceLine({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div style={{ display: "flex", gap: 10, fontSize: 12 }}>
      <span style={{ color: "#94A3B8", minWidth: 118, flexShrink: 0 }}>
        {k}
      </span>
      <span style={{ color: "#0F172A", wordBreak: "break-word" }}>{v}</span>
    </div>
  );
}

/**
 * What backs one receipt.
 *
 * Deliberately not a single "proof" concept, because a receipt's evidence
 * depends on how the money arrived and pretending otherwise is how you end up
 * with an Upload button on a bank transfer the bank already documented:
 *
 *  • reconciliation — the bank's own record. Counterpart, IBAN,
 *    Verwendungszweck, booked date. It carries no `payment_proofs` row ON
 *    PURPOSE: an approved proof marks the month settled, which is precisely
 *    the amount-aware bug COL-417 fixed for partial payments.
 *  • credit carry-forward — derived from an overpaid month, not money that
 *    arrived. Its evidence is that month, and it is corrected there.
 *  • keyed by hand (cash, a transfer an admin typed in) — the attestation IS
 *    the evidence: who recorded it, when, and against which method. A document
 *    can back it, and that document lives on the payment row, so it is offered
 *    here rather than duplicated per receipt.
 */
function ReceiptEvidence({
  receipt: r,
  bankTx,
  proof,
  onViewProof,
  onUploadProof,
  busy,
}: {
  receipt: PaymentReceipt;
  bankTx: ReceiptBankTransaction | null;
  proof: { file_name?: string | null; created_at?: string | null } | null;
  onViewProof?: (proof: any) => void;
  onUploadProof?: () => void;
  busy?: boolean;
}) {
  const { t } = useLanguage();
  const fromBank = Boolean(r.bank_transaction_id);
  const isCredit = r.source === "credit_carryover";
  return (
    <div
      style={{
        margin: "2px 0 10px",
        background: "#fff",
        border: "1px solid #E7EBF0",
        borderRadius: 8,
        padding: "10px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 7,
      }}
    >
      <div style={{ ...uppLabel, fontSize: 10.5 }}>
        {fromBank
          ? t("admin.tenantTabs.evidenceBank", "Bank record")
          : isCredit
            ? t("admin.tenantTabs.evidenceCredit", "Carried forward")
            : t("admin.tenantTabs.evidenceRecorded", "Recorded by admin")}
      </div>

      {fromBank && bankTx && (
        <>
          <EvidenceLine
            k={t("admin.tenantTabs.evidenceFrom", "From")}
            v={bankTx.counterpart_name || "—"}
          />
          {bankTx.counterpart_iban && (
            <EvidenceLine k="IBAN" v={bankTx.counterpart_iban} />
          )}
          <EvidenceLine
            k={t("admin.tenantTabs.evidencePurpose", "Reference")}
            v={bankTx.purpose || "—"}
          />
          <EvidenceLine
            k={t("admin.tenantTabs.evidenceBooked", "Booked")}
            v={`${formatDate(bankTx.booking_date) ?? "—"} · ${centsToEUR(bankTx.amount_cents)}`}
          />
          <EvidenceLine
            k={t("admin.tenantTabs.evidenceTxId", "Transaction")}
            v={`finAPI ${bankTx.finapi_transaction_id ?? "—"}`}
          />
        </>
      )}

      {fromBank && !bankTx && (
        <div style={{ fontSize: 12, color: "#94A3B8" }}>
          {t(
            "admin.tenantTabs.evidenceBankMissing",
            "The bank transaction behind this receipt is no longer available."
          )}
        </div>
      )}

      {!fromBank && (
        <>
          <EvidenceLine
            k={t("admin.tenantTabs.evidenceMethod", "Method")}
            v={
              r.method
                ? String(r.method).replace(/_/g, " ")
                : t("admin.tenantTabs.evidenceMethodNone", "not stated")
            }
          />
          <EvidenceLine
            k={t("admin.tenantTabs.evidenceReceived", "Received")}
            v={formatDate(r.received_at) ?? String(r.received_at).slice(0, 10)}
          />
          {/* The note is already on the receipt line above — repeating it here
              would be the second copy of the same sentence in 40px. */}
        </>
      )}

      {/* The document sits on the payment row, not the receipt — one month has
          one transfer slip far more often than one per part-payment. Offered
          from here so a hand-keyed receipt is not a dead end. */}
      {!isCredit && (onViewProof || onUploadProof) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 2,
            paddingTop: 8,
            borderTop: "1px solid #F1F4F8",
            flexWrap: "wrap",
          }}
        >
          {proof && onViewProof ? (
            <button
              onClick={() => onViewProof(proof)}
              style={{
                ...actionBtn("#0B7268"),
                borderColor: "#C6EEDB",
                background: "#F4FCF8",
                maxWidth: 210,
              }}
            >
              <IcEye />
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {proof.file_name ||
                  t("admin.tenantTabs.evidenceDocument", "Transfer document")}
              </span>
            </button>
          ) : onUploadProof ? (
            <button
              onClick={() => !busy && onUploadProof()}
              disabled={busy}
              style={{ ...actionBtn("#0E7490"), border: "1px dashed #C4D0DC" }}
            >
              <IcUpload />
              {t("admin.tenantTabs.uploadProof", "Upload proof")}
            </button>
          ) : null}
          <span style={{ fontSize: 11, color: "#94A3B8" }}>
            {t(
              "admin.tenantTabs.evidenceDocumentScope",
              "Document is attached to the month, not this single receipt."
            )}
          </span>
        </div>
      )}
    </div>
  );
}
